<template>
    <div class="pdf-overlay" @click.self="close">
  
      <div class="pdf-modal">
  
        <!-- TOOLBAR -->
        <div class="pdf-toolbar">
            <v-btn icon @click="zoomOut">
            <v-icon>mdi-magnify-minus</v-icon>
            </v-btn>

            <span>{{ Math.round(scale * 100) }}%</span>

            <v-btn icon @click="zoomIn">
            <v-icon>mdi-magnify-plus</v-icon>
            </v-btn>

            <div class="spacer"></div>

            <span>{{ currentPage }} / {{ totalPages }}</span>

            <div class="spacer"></div>

            <v-tooltip bottom>
            <template v-slot:activator="{ on }">
                <v-btn icon v-on="on" @click="download">
                <v-icon >mdi-download</v-icon>
                </v-btn>
            </template>
            <span>Download PDF</span>
            </v-tooltip>
            <div class="spacer"></div>

            <v-btn icon @click="close">
            <v-icon>mdi-close</v-icon>
            </v-btn>

          <!-- <button @click="zoomOut">➖</button>
          <span>{{ Math.round(scale * 100) }}%</span>
          <button @click="zoomIn">➕</button>
  
          <div class="spacer"></div>
  
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <div class="spacer"></div>
          <button @click="download">⬇</button>
          <div class="spacer"></div>
          <button @click="close">✖</button> -->
        </div>
  
        <!-- VIEWER -->
        <div
          ref="container"
          class="pdf-body"
          @touchstart="onTouchStart"
          @touchmove="onTouchMove"
          @touchend="onTouchEnd"
        >
          <div
            ref="content"
            class="pdf-content"
            :style="transformStyle"
          >
            <div
              v-for="page in totalPages"
              :key="page"
              class="pdf-page"
            >
              <canvas :ref="'page-' + page"></canvas>
            </div>
          </div>
        </div>
  
      </div>
    </div>
  </template>
  
  <script>
  import * as pdfjsLib from "pdfjs-dist/build/pdf"
  import "pdfjs-dist/build/pdf.worker.entry"
  import 'material-design-icons-iconfont/dist/material-design-icons.css'
  
  export default {
    props: {
      src: String
    },
  
    data() {
      return {
        pdf: null,
        totalPages: 0,
        currentPage: 1,
  
        // transform
        scale: 1,
        baseScale: 1,
        translateX: 0,
        translateY: 0,
  
        // gesture
        startDist: 0,
        lastTouch: null,
        isPinching: false,
        isDragging: false,
        lastTap: 0
      }
    },
  
    computed: {
      transformStyle() {
        return {
          transform: `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
          transformOrigin: "0 0"
        }
      }
    },
  
    async mounted() {
      document.body.style.overflow = "hidden"
      window.addEventListener("keydown", this.onEsc)
  
      await this.loadPDF()
    },
  
    beforeDestroy() {
      document.body.style.overflow = ""
      window.removeEventListener("keydown", this.onEsc)
    },
  
    methods: {
  
      /* ================= PDF ================= */
  
      async loadPDF() {
        const loadingTask = pdfjsLib.getDocument(this.src)
        this.pdf = await loadingTask.promise
        this.totalPages = this.pdf.numPages
  
        this.$nextTick(() => {
          this.renderPages()
        })
      },
  
      async renderPages() {
        const containerWidth = this.$refs.container.clientWidth
  
        for (let i = 1; i <= this.totalPages; i++) {
          const page = await this.pdf.getPage(i)
  
          const viewport = page.getViewport({ scale: 1 })
          const scale = containerWidth / viewport.width
  
          const canvas = this.$refs["page-" + i][0]
          const ctx = canvas.getContext("2d")
  
          canvas.width = viewport.width * scale
          canvas.height = viewport.height * scale
  
          await page.render({
            canvasContext: ctx,
            viewport: page.getViewport({ scale })
          }).promise
        }
      },
  
      /* ================= GESTURE ================= */
  
      getDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX
        const dy = touches[0].clientY - touches[1].clientY
        return Math.sqrt(dx * dx + dy * dy)
      },
  
      onTouchStart(e) {
        const now = Date.now()
  
        // double tap
        if (now - this.lastTap < 300) {
          this.scale = this.scale > 1 ? 1 : 2
          this.translateX = 0
          this.translateY = 0
          return
        }
  
        this.lastTap = now
  
        if (e.touches.length === 2) {
          this.isPinching = true
          this.startDist = this.getDistance(e.touches)
          this.baseScale = this.scale
        } else {
          this.isDragging = true
          this.lastTouch = e.touches[0]
        }
      },
  
      onTouchMove(e) {
        if (this.isPinching && e.touches.length === 2) {
          const newDist = this.getDistance(e.touches)
          let scale = (newDist / this.startDist) * this.baseScale
  
          this.scale = Math.max(0.5, Math.min(scale, 3))
        }
  
        if (this.isDragging && e.touches.length === 1) {
          const dx = e.touches[0].clientX - this.lastTouch.clientX
          const dy = e.touches[0].clientY - this.lastTouch.clientY
  
          this.translateX += dx
          this.translateY += dy
  
          this.lastTouch = e.touches[0]
        }
      },
  
      onTouchEnd() {
        this.isPinching = false
        this.isDragging = false
  
        // swipe page
        if (this.translateX > 120) this.prevPage()
        if (this.translateX < -120) this.nextPage()
  
        this.translateX = 0
      },
  
      /* ================= CONTROL ================= */
  
      zoomIn() {
        this.scale = Math.min(this.scale + 0.2, 3)
      },
  
      zoomOut() {
        this.scale = Math.max(this.scale - 0.2, 0.5)
      },
  
      nextPage() {
        if (this.currentPage < this.totalPages) this.currentPage++
      },
  
      prevPage() {
        if (this.currentPage > 1) this.currentPage--
      },
  
      download() {
        const link = document.createElement("a")
        link.href = this.src
        link.download = "file.pdf"
        link.click()
      },
  
      close() {
        this.$emit("close")
      },
  
      onEsc(e) {
        if (e.key === "Escape") this.close()
      }
    }
  }
  </script>
  
  <style scoped>

  .toolbar-btn {
    color: white;
    opacity: 0.85;
    }

  .toolbar-btn:hover {
    opacity: 1;
  }

  .toolbar-icon {
    color: white;
    opacity: 0.85;
  }
  
  .pdf-overlay {
    position: fixed;
    inset: 0;
    background: rgba(81, 80, 80, 0.8);
    z-index: 99999;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .pdf-modal {
    width: 90%;
    height: 90%;
    background: #111;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .pdf-toolbar {
    display: flex;
    align-items: center;
    padding: 10px;
    background: linear-gradient(90deg, #3df383 0%, #039233 100%);
    border-radius: 0px;
    color: #ffffff !important;
  }

  .v-icon{
    color: #ffffff !important;
  }
  
  .spacer {
    flex: 1;
  }
  
  /* .pdf-body {
    flex: 1;
    overflow: hidden;
    touch-action: none;
  } */

  .pdf-body {
    flex: 1;
    overflow-y: auto;
    touch-action: auto;
    scroll-behavior: smooth;
  }

  .pdf-content {
    will-change: transform;
  }
  
  .pdf-page {
    display: flex;
    justify-content: center;
    margin-bottom: 12px;
  }
  
  canvas {
    background: white;
    max-width: 100%;
  }
  
  @media (max-width: 600px) {
    .pdf-modal {
      width: 100%;
      height: 100%;
      border-radius: 0;
    }
  }
  
  </style>